import React, { useId, useRef, useState } from 'react';
import { Camera, ImagePlus, RotateCcw, X } from 'lucide-react';

export async function preparePhoto(file) {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    throw new Error('Choisissez une photo JPG, PNG ou WebP.');
  }
  if (file.size > 12 * 1024 * 1024) throw new Error('Cette photo dépasse 12 Mo. Choisissez un fichier plus léger.');
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    if (!image.naturalWidth || image.naturalWidth * image.naturalHeight > 60000000) {
      throw new Error('Les dimensions de cette photo sont trop grandes.');
    }
    const scale = Math.min(1, 1920 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    const data = canvas.toDataURL('image/webp', .86);
    if (Math.ceil(data.split(',')[1].length * 3 / 4) > 2 * 1024 * 1024) throw new Error('Cette photo reste trop lourde après optimisation. Choisissez un fichier plus léger.');
    return data;
  } catch (error) {
    throw new Error(error.message?.includes('photo') ? error.message : 'Cette image ne peut pas être lue. Essayez une autre photo.');
  } finally { URL.revokeObjectURL(url); }
}

export default function PhotoPicker({ value, fallback = '', onChange, label, hint, testId, disabled = false, onBusyChange }) {
  const inputId = useId();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [broken, setBroken] = useState(false);
  const image = value || fallback;
  const select = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(''); setBusy(true); onBusyChange?.(true);
    try { onChange(await preparePhoto(file)); setBroken(false); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); onBusyChange?.(false); }
  };
  return (
    <section className="photo-picker" aria-label={label}>
      <div className="photo-picker-preview">
        {image && !broken ? <img key={image} src={image} onError={() => setBroken(true)} onLoad={() => setBroken(false)} alt={label} data-testid={`${testId}-preview`} /> : <div className="photo-placeholder"><Camera size={30} /><span>{image ? 'Aperçu indisponible' : 'Aucune photo'}</span></div>}
        {busy && <span className="photo-processing" role="status">Optimisation de la photo…</span>}
      </div>
      <div className="photo-picker-content">
        <h3>{label}</h3><p>{hint || 'Choisissez une photo sur votre appareil. Elle sera optimisée automatiquement.'}</p>
        <input ref={input} id={inputId} type="file" accept="image/jpeg,image/png,image/webp" onChange={select} className="sr-only" tabIndex={-1} data-testid={testId} disabled={busy || disabled} />
        <div className="photo-picker-actions">
          <button type="button" className="admin-action admin-action-primary" onClick={() => input.current.click()} disabled={busy || disabled} data-testid={`${testId}-choose`} aria-controls={inputId}><ImagePlus size={16} />{image ? 'Changer la photo' : 'Ajouter une photo'}</button>
          {value && <button type="button" className="admin-action" disabled={busy || disabled} onClick={() => { onChange(''); setBroken(false); setError(''); }} data-testid={`${testId}-clear`}>{fallback ? <RotateCcw size={15} /> : <X size={15} />}{fallback ? 'Photo d’origine' : 'Retirer'}</button>}
        </div>
        <small>JPG, PNG ou WebP · 12 Mo max à l’importation</small>
        {error && <p role="alert" className="admin-error" data-testid={`${testId}-error`}>{error}</p>}
      </div>
    </section>
  );
}
